import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, PanelLeftClose, ChevronRight, Plus, Trash2, Flag } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

interface ConversationSidebarProps {
  sessionId: string;
  patientName: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onNewChat?: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

interface ChatMessage {
  id: number;
  conversationId: string;
  message: string;
  response: string;
  createdAt: string;
  tokensUsed?: number;
  modelUsed?: string;
  responseTimeMs?: number;
  title?: string;
  isTitleGenerated?: boolean;
}

export default function ConversationSidebar({
  sessionId,
  isCollapsed = false,
  onToggleCollapse,
  onNewChat,
  onSelectConversation,
}: ConversationSidebarProps) {
  const { t } = useTranslation();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [isDeletingMode, setIsDeletingMode] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch recent chat messages for this session
  const { data: chatMessages = [] } = useQuery({
    queryKey: ["/api/chat-messages", sessionId],
    queryFn: () => api.getChatMessages(),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Batch delete mutation
  const batchDeleteMutation = useMutation({
    mutationFn: (conversationIds: string[]) => api.batchDeleteConversations(conversationIds),
    onMutate: async (conversationIds) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/chat-messages", sessionId] });

      // Snapshot previous value
      const previousMessages = queryClient.getQueryData<ChatMessage[]>(["/api/chat-messages", sessionId]);

      // Optimistically remove conversations from cache
      queryClient.setQueryData<ChatMessage[]>(
        ["/api/chat-messages", sessionId],
        (old = []) => old.filter(msg => !conversationIds.includes(msg.conversationId))
      );

      return { previousMessages };
    },
    onSuccess: (data, conversationIds) => {
      toast({
        title: t('records.conversationsDeletedTitle'),
        description: t('records.conversationsDeletedDescription', { count: data.deletedCount }),
      });

      // Exit delete mode and clear selections
      setIsDeletingMode(false);
      setSelectedForDeletion(new Set());

      // If current conversation was deleted, switch to most recent remaining
      if (selectedConversation && conversationIds.includes(selectedConversation)) {
        const remainingMessages = chatMessages.filter(msg => !conversationIds.includes(msg.conversationId));
        if (remainingMessages.length > 0) {
          const mostRecent = remainingMessages[0];
          setSelectedConversation(mostRecent.conversationId);
          onSelectConversation?.(mostRecent.conversationId);
        }
      }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(["/api/chat-messages", sessionId], context.previousMessages);
      }
      // Exit delete mode so user isn't stuck
      setIsDeletingMode(false);
      setSelectedForDeletion(new Set());
      toast({
        title: t('common.errorTitle'),
        description: error instanceof Error ? error.message : t('records.deleteConversationsFailed'),
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["/api/chat-messages", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["/api/chat-messages/current"] });
    },
  });

  const handleToggleSelection = (conversationId: string) => {
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedForDeletion.size === 0) return;
    batchDeleteMutation.mutate(Array.from(selectedForDeletion));
  };

  const handleCancelDelete = () => {
    setIsDeletingMode(false);
    setSelectedForDeletion(new Set());
  };

  // Helper function to group conversations by date
  const groupConversationsByDate = (messages: ChatMessage[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: { [key: string]: ChatMessage[] } = {
      [t('records.groupToday')]: [],
      [t('records.groupYesterday')]: [],
      [t('records.groupPrevious7Days')]: [],
      [t('records.groupPrevious30Days')]: [],
    };

    messages.forEach((msg) => {
      const msgDate = new Date(msg.createdAt);
      msgDate.setHours(0, 0, 0, 0);

      const diffTime = today.getTime() - msgDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        groups[t('records.groupToday')].push(msg);
      } else if (diffDays === 1) {
        groups[t('records.groupYesterday')].push(msg);
      } else if (diffDays <= 7) {
        groups[t('records.groupPrevious7Days')].push(msg);
      } else if (diffDays <= 30) {
        groups[t('records.groupPrevious30Days')].push(msg);
      }
    });

    return groups;
  };

  const conversationGroups = groupConversationsByDate(chatMessages.slice(-50).reverse());

  if (isCollapsed) {
    return (
      <div className="hidden md:flex flex-col items-center bg-gray-50 border-r border-gray-200 p-2 w-12">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="mb-4"
          aria-label={t('records.expandSidebar')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <MessageSquare className="h-5 w-5 text-gray-400" />
      </div>
    );
  }

  return (
    <div className="hidden md:flex flex-col bg-gray-50 border-r border-gray-200 w-64">
      {/* Header with Menu Icon */}
      <div className="flex items-center justify-start p-3 border-b border-gray-200">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-7 w-7"
          aria-label={t('records.toggleSidebar')}
        >
          <PanelLeftClose className="h-4 w-4" />
        </Button>
      </div>

      {/* New Chat Button */}
      <div className="p-3">
        <Button
          onClick={onNewChat}
          className="w-full bg-primary hover:bg-primary/90 text-white"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('records.newChat')}
        </Button>
      </div>

      {/* Conversations List */}
      <ScrollArea className="flex-1">
        <div className="px-2 pb-2">
          {Object.entries(conversationGroups).map(([groupName, messages]) => {
            if (messages.length === 0) return null;

            return (
              <div key={groupName} className="mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 mb-2">
                  {groupName}
                </h3>
                <div className="space-y-1">
                  {messages.map((msg) => {
                    // Use first 4 words of the message as the title
                    const words = msg.message.trim().split(/\s+/);
                    const titleWords = words.slice(0, 4).join(' ');
                    const displayTitle = titleWords.length > 35 ? titleWords.substring(0, 35) + '...' : titleWords;

                    const isCurrentConversation = selectedConversation === msg.conversationId;
                    const isSelected = selectedForDeletion.has(msg.conversationId);

                    return (
                      <div
                        key={msg.id}
                        className={`w-full px-3 py-2 rounded-md transition-colors group ${
                          isCurrentConversation ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                        }`}
                      >
                        {isDeletingMode ? (
                          // Delete mode: Show checkbox
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">
                              <Checkbox
                                id={`conversation-${msg.conversationId}`}
                                checked={isSelected}
                                onCheckedChange={() => handleToggleSelection(msg.conversationId)}
                                disabled={isCurrentConversation}
                                aria-label={isCurrentConversation ? t('records.cannotDeleteCurrentConversation') : t('records.selectForDeletion')}
                              />
                            </div>
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <span className={`text-xs break-words line-clamp-2 ${isCurrentConversation ? 'text-gray-400' : 'text-text-primary'}`}>
                                {displayTitle || t('records.newConversation')}
                              </span>
                            </div>
                          </div>
                        ) : (
                          // Normal mode: Clickable button
                          <button
                            onClick={() => {
                              setSelectedConversation(msg.conversationId);
                              onSelectConversation?.(msg.conversationId);
                            }}
                            className="w-full text-left"
                          >
                            <div className="flex items-start gap-2">
                              <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <span className="text-xs text-text-primary break-words line-clamp-2">
                                {displayTitle || t('records.newConversation')}
                              </span>
                            </div>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {chatMessages.length === 0 && (
            <div className="p-4 text-center">
              <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-text-secondary">
                {t('records.noChatHistory')}
              </p>
              <p className="text-xs text-text-secondary mt-1">
                {t('records.clickNewChatToStart')}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Delete Mode Actions or Delete Button */}
      {isDeletingMode ? (
        // Action bar when in delete mode
        <div className="p-3 border-t border-gray-200 space-y-2">
          <Button
            onClick={handleDeleteSelected}
            disabled={selectedForDeletion.size === 0 || batchDeleteMutation.isPending}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            size="sm"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('records.deleteConversationsCount', { count: selectedForDeletion.size })}
          </Button>
          <Button
            onClick={handleCancelDelete}
            variant="outline"
            className="w-full"
            size="sm"
            disabled={batchDeleteMutation.isPending}
          >
            {t('records.cancel')}
          </Button>
        </div>
      ) : (
        <div className="p-3 border-t border-gray-200 space-y-2">
          {/* Delete button (only show when > 1 conversation) */}
          {chatMessages.length > 1 && (
            <Button
              onClick={() => setIsDeletingMode(true)}
              variant="outline"
              className="w-full text-gray-600 hover:text-red-600 hover:border-red-300 hover:bg-red-50"
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('records.deleteConversations')}
            </Button>
          )}
          {/* Report a Concern */}
          <Button
            onClick={() => { window.location.href = 'mailto:info@mylitmed.com?subject=LitMed%20AI%20-%20Concern%20Report'; }}
            variant="outline"
            className="w-full text-gray-600 hover:text-gray-800"
            size="sm"
          >
            <Flag className="h-4 w-4 mr-2" />
            {t('records.reportConcern')}
          </Button>
        </div>
      )}
    </div>
  );
}
